// Advanced Calculations Tooltips
export const advancedCalculationsTooltips = {
  // Financial Statements
  revenue: "Total income generated from all revenue streams and products. Sum of all sales forecasts for the selected year.",
  
  cogs: "Cost of Goods Sold - Direct costs attributable to production of goods/services sold. Includes variable costs and direct costs from cost items.",
  
  grossProfit: "Revenue minus Cost of Goods Sold. Indicates how efficiently you're producing your products/services before operating expenses.",
  
  grossMargin: "Gross Profit as a percentage of Revenue. Higher margins indicate better production efficiency. Formula: (Gross Profit / Revenue) × 100",
  
  operatingExpenses: "Fixed and indirect costs of running the business. Includes salaries, rent, marketing, utilities, and other overhead costs.",
  
  ebitda: "Earnings Before Interest, Taxes, Depreciation, and Amortization. A measure of operational profitability. Formula: Gross Profit - Operating Expenses",
  
  depreciation: "Non-cash expense representing the decrease in value of fixed assets over time. Calculated using straight-line method from CapEx items.",
  
  ebit: "Earnings Before Interest and Taxes. Operating profit after depreciation. Formula: EBITDA - Depreciation",
  
  interest: "Interest expense on loans and other debt financing. Based on loan amounts, interest rates, and financial cost items.",
  
  pbt: "Profit Before Tax. Total profit before tax obligations. Formula: EBIT - Interest",
  
  taxes: "Income tax expense calculated based on tax rates and rules defined in tax items. Applied to PBT if positive.",
  
  netIncome: "Bottom line profit after all expenses and taxes. Formula: PBT - Taxes. This is the final profitability measure.",
  
  netMargin: "Net Income as a percentage of Revenue. Shows overall profitability. Formula: (Net Income / Revenue) × 100",
  
  // Balance Sheet
  cash: "Cash and cash equivalents. Simplified calculation: 10% of Net Income (can be enhanced with detailed cash flow).",
  
  accountsReceivable: "Money owed by customers for goods/services delivered. Simplified calculation: 8% of annual revenue.",
  
  fixedAssets: "Long-term tangible assets like equipment, buildings, vehicles. Sum of all CapEx items minus accumulated depreciation.",
  
  totalAssets: "Everything the company owns. Formula: Cash + Accounts Receivable + Fixed Assets",
  
  accountsPayable: "Money owed to suppliers and vendors. Simplified calculation: 5% of annual revenue.",
  
  loans: "Outstanding debt balance from all loans. Sum of remaining principal balances on all active loans.",
  
  totalLiabilities: "Everything the company owes. Formula: Accounts Payable + Loans",
  
  paidInCapital: "Money invested by shareholders/owners. Sum of all equity investments and raises.",
  
  retainedEarnings: "Accumulated profits kept in the business. Calculated to balance the Balance Sheet equation.",
  
  totalEquity: "Owner's stake in the company. Formula: Paid-in Capital + Retained Earnings. Also equals: Total Assets - Total Liabilities",
  
  // Cash Flow
  operatingCashFlow: "Cash generated from core business operations. Formula: Net Income + Depreciation - Change in Working Capital",
  
  investingCashFlow: "Cash used for investments in long-term assets. Primarily CapEx spending (shown as negative).",
  
  financingCashFlow: "Cash from/to investors and lenders. Includes debt raised, debt repayments, and equity raises.",
  
  netCashFlow: "Net change in cash position. Formula: Operating CF + Investing CF + Financing CF",
  
  // Break-Even Analysis
  breakEvenUnits: "Number of units you must sell annually to cover all costs (revenue = total costs). Formula: Annual Fixed Costs ÷ Contribution Margin per Unit. Based on current product pricing and cost structure.",
  
  breakEvenRevenue: "Annual dollar amount of sales needed to break even. Formula: Break-Even Units × Average Selling Price. This is your minimum revenue target to avoid losses.",
  
  contributionMargin: "Amount each unit contributes to covering fixed costs and profit. Formula: Selling Price - Variable Cost per Unit",
  
  contributionMarginPercent: "Contribution Margin as percentage of selling price. Higher is better. Formula: (Contribution Margin ÷ Price) × 100",
  
  marginOfSafety: "How much sales can drop before reaching break-even point. Shows cushion above break-even. Formula: (Actual Sales - Break-Even Sales) ÷ Actual Sales × 100",
  
  fixedCosts: "Costs that don't vary with production volume. Includes rent, salaries, insurance. Must be covered regardless of sales.",
  
  variableCosts: "Costs that vary directly with production volume. Includes raw materials, direct labor, packaging.",
  
  averageSellingPrice: "Weighted average price per unit across all products. Calculated from product units and prices.",
  
  currentPosition: "Your actual current sales vs break-even point. Shows whether you're above or below break-even.",
  
  // ROI Analysis
  totalInitiatives: "Number of strategic initiatives being analyzed for ROI. From Strategic Framework.",
  
  totalInvestment: "Total capital invested across all initiatives. Sum of CapEx, cost allocations, and cost items linked to initiatives.",
  
  annualProfit: "Net annual profit generated by the initiative. Formula: Annual Revenue - Annual Operating Costs. All ROI calculations are ANNUALIZED for comparability.",
  
  portfolioROI: "Overall return on investment across all initiatives. Formula: (Total Annual Profit ÷ Total Investment) × 100. Annualized returns. Higher is better.",
  
  averageROI: "Average ROI across all initiatives. Simple arithmetic mean of individual initiative ROIs. Annualized basis.",
  
  initiativeROI: "Return on investment for this specific initiative. Formula: (Annual Profit ÷ Investment Base) × 100. ANNUALIZED - shows yearly return rate. If no upfront investment (CapEx/allocations), uses operating costs as base.",
  
  paybackPeriod: "Time to recover initial investment from upfront CapEx or cost allocations. Formula: Investment ÷ Annual Profit. Shows 'N/A' if initiative has NO upfront investment (only operating costs). Measured in years. Lower is better.",
  
  npv: "Net Present Value over 5 years. Accounts for time value of money using 10% discount rate. Positive NPV means profitable investment.",
  
  // Cash Flow Projection
  beginningCash: "Cash balance at start of the year. Equals ending cash from previous year.",
  
  endingCash: "Cash balance at end of the year. Formula: Beginning Cash + Net Cash Flow",
  
  capexSpending: "Capital expenditure for long-term assets. Investments in equipment, facilities, technology.",
  
  cashRunway: "Number of months the company can operate with current cash. Formula: Ending Cash ÷ Monthly Burn Rate",
  
  totalOperatingCF: "Sum of operating cash flow over 5-year projection period. Indicates cash generation capability.",
  
  totalCapex: "Sum of all capital expenditures over 5-year period. Shows investment intensity.",
  
  finalCashBalance: "Projected cash position at end of 5-year period. Positive indicates financial health.",
  
  averageCashRunway: "Average number of months of runway across 5-year period. Higher is safer."
};

// Help text for each tab
export const tabHelpText = {
  financial: "Comprehensive view of your financial position including Income Statement, Balance Sheet, and Cash Flow Statement. Based on your revenue streams, cost items, and financial planning.",
  
  breakeven: "Analyze the minimum sales volume needed to cover all costs. Useful for pricing decisions and understanding your financial risk. Requires products with pricing and cost items.",
  
  roi: "Evaluate return on investment for strategic initiatives. Links investments (CapEx, allocations) to returns (products, revenue). Shows which initiatives are most profitable.",
  
  cashflow: "5-year projection of cash inflows and outflows. Critical for understanding liquidity and planning for capital needs. Shows operating, investing, and financing activities."
};

// Metric interpretations
export const metricInterpretations = {
  grossMargin: {
    excellent: "> 60%",
    good: "40-60%",
    acceptable: "20-40%",
    poor: "< 20%"
  },
  netMargin: {
    excellent: "> 20%",
    good: "10-20%",
    acceptable: "5-10%",
    poor: "< 5%"
  },
  contributionMargin: {
    excellent: "> 50%",
    good: "30-50%",
    acceptable: "15-30%",
    poor: "< 15%"
  },
  roi: {
    excellent: "> 50%",
    good: "20-50%",
    acceptable: "10-20%",
    poor: "< 10%"
  },
  payback: {
    excellent: "< 1 year",
    good: "1-2 years",
    acceptable: "2-4 years",
    poor: "> 4 years"
  }
};
